import { BaseUrlStringCodec } from 'app/config/utils';
import * as t from 'io-ts';

export enum ModuleName {
  LessonBookings = 'LessonBookings',
}

const ModuleNameCodec: t.Type<ModuleName, string> = t.keyof({
  [ModuleName.LessonBookings]: null,
});

export enum ReamazeModuleType {
  Internal = 'Internal',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  External = 'External',
}

/**
 * Internal modules are rendered within the application and bind a ModuleName to a component.
 */
interface IReamazeInternalModule {
  moduleName: ModuleName;
  type: ReamazeModuleType.Internal;
}

const ReamazeInternalModuleCodecHelper: t.Type<IReamazeInternalModule, ModuleName, ModuleName> = new t.Type(
  'ReamazeInternalModuleCodecHelper',
  (x): x is IReamazeInternalModule => {
    const codec: t.Type<IReamazeInternalModule, unknown> = t.strict({
      moduleName: ModuleNameCodec,
      type: t.literal(ReamazeModuleType.Internal),
    });
    return codec.is(x);
  },
  x => t.success({ moduleName: x, type: ReamazeModuleType.Internal }),
  x => x.moduleName,
);

const ReamazeInternalModuleCodec: t.Type<IReamazeInternalModule, string> = ModuleNameCodec.pipe(
  ReamazeInternalModuleCodecHelper,
  'ReamazeInternalModuleCodec',
);

/**
 * External modules are modules that come from other sources and will be rendered within an iframe.
 */
interface IReamazeExternalModule {
  source: string;
  type: ReamazeModuleType.External;
}

const ReamazeExternalModuleCodecHelper: t.Type<IReamazeExternalModule, string, string> = new t.Type(
  'ReamazeExternalModuleHelper',
  (x): x is IReamazeExternalModule => {
    const codec: t.Type<IReamazeExternalModule, unknown> = t.strict({
      source: t.string,
      type: t.literal(ReamazeModuleType.External),
    });
    return codec.is(x);
  },
  x => t.success({ source: x, type: ReamazeModuleType.External }),
  x => x.source,
);

const ReamazeExternalModuleCodec: t.Type<IReamazeExternalModule, string> = BaseUrlStringCodec.pipe(
  ReamazeExternalModuleCodecHelper,
  'ReamazeExternalModule',
);

export type ReamazeModule = IReamazeExternalModule | IReamazeInternalModule;

interface IReamazeSerialized {
  customModule: {
    modules: string[];
  };
}

interface IReamaze {
  customModule: {
    modules: ReamazeModule[];
  };
}

const ReamazeCodec: t.Type<IReamaze, IReamazeSerialized> = t.strict({
  customModule: t.strict({
    modules: t.array(
      t.union([
        // Check internal modules first, since they contain reserved values.
        ReamazeInternalModuleCodec,
        ReamazeExternalModuleCodec,
      ]),
    ),
  }),
}, 'Reamaze');

export default ReamazeCodec;
